import { useCallback, useEffect, useState } from "react";
import type { ServerMessage, SubscribePayload } from "./types";
import type { FileData } from "@/services/files";
import { StatusFile } from "@/common/enums/enums";
import {
  SummarizationClientEvents,
  SummarizationServerEvents,
} from "@/hooks/summarization_realtime/constants";
import { useWebSocket } from "@/hooks/summarization_realtime/useWebSocket";
import { getCurrentUser } from "@/services/auth";

const WEBSOCKET_URL = import.meta.env.VITE_REALTIME_SOCKET_SERVER_URL;

export const useSummarizationSocket = (
  file: FileData
): {
  result: string | null;
  isLoading: boolean;
  error: string | null;
} => {
  const [result, setResult] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const handleOpen = useCallback(
    async (ws: WebSocket) => {
      setIsLoading(true);
      setError(null);
      setResult(null);
      try {
        const user = await getCurrentUser();
        if (!user) throw new Error("Sign in to receive summarization.");
        const token = await user.getIdToken(true);
        if (!token) throw new Error("Unable to authenticate summarization.");
        if (ws.readyState !== WebSocket.OPEN) {
          setIsLoading(false);
          return;
        }
        const payload: SubscribePayload = {
          action: SummarizationClientEvents.SUMMARIZATION_SUBSCRIBE,
          fileId: file.id,
          token,
        };
        ws.send(JSON.stringify(payload));
      } catch {
        setIsLoading(false);
        setError("Unable to authenticate summarization. Please sign in again.");
      }
    },
    [file.id]
  );

  const handleMessage = useCallback((event: MessageEvent) => {
    try {
      const raw = typeof event.data === "string" ? event.data : "";
      if (!raw) return;
      const message: ServerMessage = JSON.parse(raw) as ServerMessage;
      switch (message.type) {
        case SummarizationServerEvents.SUMMARIZATION_CHUNK:
          setResult(message.data.result ?? null);
          break;
        case SummarizationServerEvents.SUMMARIZATION_FINAL:
          setResult(message.data.result ?? null);
          setIsLoading(false);
          break;
        case SummarizationServerEvents.SUMMARIZATION_ERROR:
          setIsLoading(false);
          setError(
            ("error" in message.data ? message.data.error : undefined) ??
              message.data.result ??
              "Socket error"
          );
          break;
        default:
          setIsLoading(false);
          setError("Socket error");
          break;
      }
    } catch {
      setIsLoading(false);
      setError("Socket error");
    }
  }, []);

  const handleClose = useCallback((): void => {
    setIsLoading(false);
  }, []);
  const handleError = useCallback(() => {
    setIsLoading(false);
    setError("Socket error");
  }, []);

  const { connect, disconnect } = useWebSocket({
    url: WEBSOCKET_URL,
    onOpen: handleOpen,
    onMessage: handleMessage,
    onClose: handleClose,
    onError: handleError,
  });

  useEffect(() => {
    const shouldConnect =
      file.status === StatusFile.PROCESSING.toString() ||
      file.status === StatusFile.GENERATING.toString();

    if (shouldConnect) {
      connect();
    } else {
      disconnect();
    }
    return (): void => {
      disconnect();
    };
  }, [file.id, file.status, connect, disconnect]);

  return {
    result,
    isLoading,
    error,
  };
};
